import { createTheme, ThemeProvider, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import {
  NextImage,
  type ImageField,
  type ImageFieldValue,
} from '@sitecore-jss/sitecore-jss-nextjs';
import ButtonBase from './ButtonBase';
import { ButtonBaseProps, GradientButtonTheme } from './button.types';
import { applyGradientButtonStyles } from './button.utility';
import { useMemo } from 'react';
import { DEFAULT_PRIMARY_FONT_FAMILY } from 'theme/fontDefaults';

const FALLBACK_GRADIENT: GradientButtonTheme = {
  background: {
    default: 'linear-gradient(90deg, #01B9B0 0%, #015989 100%)',
    hover: 'linear-gradient(90deg, #01B9B0 0%, #01334E 100%)',
    pressed: 'linear-gradient(90deg, #03948D 0%, #01314A 100%)',
  },
};

type GradientButtonProps = Omit<ButtonBaseProps, 'iconLeft' | 'iconRight'> & {
  /** Sitecore image field for the circular leading asset (Experience Editor + media URLs). */
  leadingImageField?: ImageField | ImageFieldValue;
};

/**
 * True when the field can render an image (non-empty `src` or Experience Editor markup).
 * Empty `value: {}` is not enough — `{}` is truthy in JS but has no `src`, which caused a broken img.
 */
function hasLeadingImageFieldContent(field?: ImageField | ImageFieldValue | null): boolean {
  if (!field) return false;
  const f = field as ImageField & ImageFieldValue;
  if (typeof f.editable === 'string' && f.editable.trim() !== '') return true;
  const topSrc = typeof f.src === 'string' ? f.src.trim() : '';
  if (topSrc) return true;
  const v = f.value as ImageFieldValue | undefined;
  if (v && typeof v.src === 'string' && v.src.trim() !== '') return true;
  return false;
}

/** Figma asset frame: fixed circle + border + stacked shadows (#0000001A). */
const GRADIENT_CTA_ICON = {
  width: 47,
  height: 47.79661178588867,
  borderWidth: 1.41,
  boxShadow:
    '0px 3.01px 3.76px -2.26px rgba(0, 0, 0, 0.1), 0px 7.53px 9.41px -1.88px rgba(0, 0, 0, 0.1)',
} as const;

const gradientLeadingImageWrapperSx = {
  position: 'relative',
  width: GRADIENT_CTA_ICON.width,
  height: GRADIENT_CTA_ICON.height,
  flexShrink: 0,
  alignSelf: 'center',
  opacity: 1,
  borderRadius: '50%',
  border: `${GRADIENT_CTA_ICON.borderWidth}px solid rgba(255, 255, 255, 0.7)`,
  boxShadow: GRADIENT_CTA_ICON.boxShadow,
  overflow: 'hidden',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  lineHeight: 0,
} as const;

function gradientLeadingImageField(field: ImageField | ImageFieldValue) {
  return (
    <Box component="span" sx={gradientLeadingImageWrapperSx}>
      <NextImage
        field={field}
        fill
        sizes="48px"
        style={{ objectFit: 'cover', objectPosition: 'center' }}
      />
    </Box>
  );
}

function ButtonGradient({ children, leadingImageField, ...rest }: GradientButtonProps) {
  const theme = useTheme();
  const gradient: GradientButtonTheme = {
    ...FALLBACK_GRADIENT,
    ...theme.buttonStyle?.gradient,
  };
  const primaryText = theme.buttonStyle?.primary?.textColor;
  const textColorFallback = theme.palette?.white?.main ?? '#FFFFFF';
  const textColor = primaryText?.default ?? textColorFallback;
  const textColorHover = primaryText?.hover ?? textColorFallback;
  const textColorPressed = primaryText?.pressed ?? textColorFallback;
  const labelFontFamily = `${
    theme.palette.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY
  }, sans-serif`;

  const resolvedIconLeft = hasLeadingImageFieldContent(leadingImageField)
    ? gradientLeadingImageField(leadingImageField as ImageField | ImageFieldValue)
    : undefined;

  const themeOb = useMemo(
    () =>
      createTheme({
        palette: {
          ...theme.palette,
          primary: {
            main: theme.palette.primary.main,
            dark: theme.palette.primary.dark,
          },
        },
        components: {
          MuiButtonBase: {
            defaultProps: {
              disableRipple: true,
            },
          },
          MuiButton: {
            styleOverrides: {
              contained: {
                ...applyGradientButtonStyles(gradient, {
                  textColor,
                  textColorHover,
                  textColorPressed,
                  hasStartIcon: Boolean(resolvedIconLeft),
                  fontFamily: labelFontFamily,
                }),
                textTransform: 'none',
              },
            },
          },
        },
      }),
    [
      gradient,
      textColor,
      textColorHover,
      textColorPressed,
      resolvedIconLeft,
      labelFontFamily,
      theme.palette,
      theme.buttonStyle?.gradient,
      theme.buttonStyle?.primary,
    ]
  );

  return (
    <ThemeProvider theme={themeOb}>
      <ButtonBase
        variant="gradient"
        iconLeft={resolvedIconLeft}
        gap={resolvedIconLeft ? '8px' : '7px'}
        {...rest}
      >
        {children}
      </ButtonBase>
    </ThemeProvider>
  );
}

export { hasLeadingImageFieldContent };
export default ButtonGradient;
